<template>
	<div>
		<!-- Lesson name -->
		<div class="flex p2">
			<div class="fs4 fw7">{{ $t(lesson.name || "---") }}</div>
			<div class="cu2" @click="openEditModal('name')">
				<crm-icon name="edit" size="small" color="c2" />
			</div>
		</div>
		<!-- Lesson details -->
		<j-card class="xs-12 p4 bg5 mb4">
			<div class="flex xs-12 xs-between">
				<div>
					<div class="flex">
						<div class="fs3 fw7">{{ $t("description") }}</div>
						<div class="cu2" @click="openEditModal('description')">
							<crm-icon name="edit" size="small" color="c2" />
						</div>
					</div>
					<div class="fs3 lh125">{{ lesson.description || "---" }}</div>
				</div>
			</div>
			<div class="flex xs12 pt4 xs-between">
				<div>
					<div class="flex">
						<div class="fs3 fw7">{{ $t("estimated_duration") }}</div>
						<div class="cu2" @click="openEditModal('durationInSeconds')">
							<crm-icon name="edit" size="small" color="c2" />
						</div>
					</div>
					<div class="fs3">{{ computedDuration }}</div>
				</div>
				<div>
					<div class="flex">
						<div class="fs3 fw7">{{ $t("weight") }}</div>
						<div class="cu2" @click="openEditModal('weight')">
							<crm-icon name="edit" size="small" color="c2" />
						</div>
					</div>
					<div class="fs3">{{ lesson.weight }}</div>
				</div>
				<div>
					<div class="flex">
						<div class="fs3 fw7">{{ $t("passing_ratio") }}</div>
						<div class="cu2" @click="openEditModal('passingRatio')">
							<crm-icon name="edit" size="small" color="c2" />
						</div>
					</div>
					<div class="fs3">{{ lesson.passingRatio }}</div>
				</div>
				<div>
					<div class="flex">
						<div class="fs3 fw7">{{ $t("version") }}</div>
						<div class="cu2" @click="openEditModal('version')">
							<crm-icon name="edit" size="small" color="c2" />
						</div>
					</div>
					<div class="fs3">{{ lesson.version }}</div>
				</div>
			</div>
		</j-card>

		<!-- Toggle editor/preview -->
		<div>
			<j-tabs
				:selectedTab="editorOrPreview"
				:tabs="[
					{
						name: $t('editor'),
						value: 'editor'
					},
					{
						name: $t('preview'),
						value: 'preview'
					}
				]"
				@click="onChangeMode"
			/>
		</div>

		<!-- Editor or preview -->
		<lesson-editor
			v-if="lesson && editorOrPreview === 'editor'"
			:courseId="courseId"
			:lesson="lesson"
			:selectedLocale="selectedLocale"
			@item:added="$emit('lesson:updated')"
			@item:updated="$emit('lesson:updated')"
			@item:removed="$emit('lesson:updated')"
		/>
		<lesson-preview
			v-if="lesson && editorOrPreview === 'preview'"
			:lesson="lesson"
		/>

		<!-- Edit field modal -->
		<core-modal name="editFieldModal" :headline="editForm.name">
			<div class="p4 xs-12">
				<div class="xs-12 fw7 fs4 pb2">{{ editForm.name }}</div>
				<component :is="editForm.componentName" v-model="editForm.value" />
				<div class="p2 xs-12 flex xs-center">
					<j-button @click="editForm.handler">{{ $t("update") }}</j-button>
				</div>
			</div>
		</core-modal>
	</div>
</template>

<script>
import LessonEditor from "../components/LessonEditor"
import LessonPreview from "../components/LessonPreview"
import JCard from "@/components/JCard"
import JTabs from "@/components/JTabs"
import JButton from "@/components/JButton"
import JSpinner from "@/components/JSpinner"
import CrmIcon from "@/components/CrmIcon"
import CoreModal from "@/modules/core/components/CoreModal"
import JBaseInputText from "@/components/JBaseInputText"
import JBaseInputLongText from "@/components/JBaseInputLongText"
import JBaseInputNumber from "@/components/JBaseInputNumber"
import InputDurationInSeconds from "./InputDurationInSeconds"
import api from "@/api/gql.js"
import queries from "../utils/queries.js"
import { checkSuccess } from "../utils/helpers.js"

export default {
	components: {
		LessonEditor,
		LessonPreview,
		JTabs,
		JCard,
		JButton,
		JSpinner,
		CrmIcon,
		CoreModal,
		JBaseInputText,
		JBaseInputLongText,
		JBaseInputNumber,
		InputDurationInSeconds
	},

	props: {
		courseId: String,
		sectionId: String,
		lesson: Object,
		selectedLocale: String
	},

	computed: {
		componentId() {
			return this?.lesson?.component?.id
		},
		componentName() {
			return this.lesson?.component?.name
		},
		computedDuration() {
			const durationInSeconds = this.lesson?.durationInSeconds || 0
			return `${parseInt(durationInSeconds / 3600)}h ${(durationInSeconds %
				3600) /
				60}min`
		}
	},

	data() {
		return {
			isLoading: false,
			lessonEditor: LessonEditor,
			lessonPreview: LessonPreview,
			editorOrPreview: "editor",
			tabs: [
				{
					id: "overview",
					name: this.$t("overview"),
					to: { name: "CourseDetail", params: { ...this.$route.params } }
				},
				{
					id: "content",
					name: this.$t("content"),
					to: { name: "CourseContent", params: { ...this.$route.params } }
				}
			],

			editForm: {
				name: null,
				value: null,
				handler: null,
				componentName: null
			}
		}
	},

	methods: {
		onChangeMode(payload) {
			this.editorOrPreview = payload
			// Need to refetch, because for HTML items we cannot modify the item itself (to avoid cursor to jump at the beginning)
			this.$emit("lesson:updated")
		},

		openEditModal(fieldName) {
			if (fieldName === "passingRatio") {
				this.editForm.value = this.lesson?.passingRatio
				this.editForm.name = this.$t("passing_ratio")
				this.editForm.componentName = "j-base-input-number"
				this.editForm.handler = () => this.updateLessonField(fieldName)
			}
			if (fieldName === "weight") {
				this.editForm.value = this.lesson?.weight
				this.editForm.name = this.$t("weight")
				this.editForm.componentName = "j-base-input-number"
				this.editForm.handler = () => this.updateLessonField(fieldName)
			}
			if (fieldName === "version") {
				this.editForm.value = this.lesson?.version
				this.editForm.name = this.$t("version")
				this.editForm.componentName = "j-base-input-text"
				this.editForm.handler = () => this.updateLessonField(fieldName)
			}
			if (fieldName === "durationInSeconds") {
				this.editForm.value = this.lesson?.durationInSeconds
				this.editForm.name = this.$t("estimated_duration")
				this.editForm.componentName = "input-duration-in-seconds"
				this.editForm.handler = () => this.updateLessonField(fieldName)
			}
			if (fieldName === "description") {
				this.editForm.value = this.lesson?.description
				this.editForm.name = this.$t("description")
				this.editForm.componentName = "j-base-input-long-text"
				this.editForm.handler = () => this.upsertL10n("description")
			}
			if (fieldName === "name") {
				this.editForm.value = this.lesson?.name
				this.editForm.name = this.$t("name")
				this.editForm.componentName = "j-base-input-text"
				this.editForm.handler = () => this.upsertL10n("name")
			}
			this.$modal.show("editFieldModal")
		},

		async updateLessonField(fieldName) {
			let variables = {
				courseId: this.courseId,
				sectionId: this.sectionId,
				lessonId: this.lesson.id,
				input: {
					[fieldName]: this.editForm.value
				}
			}

			let res = await api({ query: queries.updateLessonOfSection, variables })

			if (
				!checkSuccess(
					res,
					"updateLessonOfSection",
					this.$t("errors.general"),
					this.$store
				)
			)
				return
			this.$modal.hide("editFieldModal")
			this.$emit("lesson:updated")
		},

		async upsertL10n(type) {
			const variables = {
				l10n: {
					key: `lessons-${this.lesson.id}-${type}`,
					locale: this.selectedLocale,
					value: this.editForm.value || ""
				}
			}

			const res = await api({ query: queries.upsertL10n, variables })
			if (
				!checkSuccess(res, "upsertL10n", this.$t("errors.general"), this.$store)
			)
				return
			this.$modal.hide("editFieldModal")
			this.$emit("lesson:updated")
		}
	}
}
</script>
